import { useState } from "react";
import { Check } from "lucide-react";
import OrangeCTA from "./OrangeCTA";

const WEB3FORMS_ACCESS_KEY = "6420eec9-0b2c-4dc4-b90c-68a44a6c9e27";

const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com","googlemail.com","hotmail.com","hotmail.com.br","outlook.com","outlook.com.br",
  "live.com","live.com.br","msn.com","yahoo.com","yahoo.com.br","ymail.com","rocketmail.com",
  "icloud.com","me.com","mac.com","aol.com","proton.me","protonmail.com","pm.me",
  "gmx.com","gmx.net","mail.com","bol.com.br","uol.com.br","terra.com.br","ig.com.br",
  "r7.com","zipmail.com.br","oi.com.br","globo.com","yandex.com","tutanota.com","zoho.com",
]);

function isFreeEmail(email: string) {
  const at = email.lastIndexOf("@");
  if (at === -1) return false;
  const domain = email.slice(at + 1).trim().toLowerCase();
  return FREE_EMAIL_DOMAINS.has(domain);
}

function Field({
  name,
  placeholder,
  type = "text",
  required = false,
  full = false,
  inputMode,
  error,
  onChange,
}: {
  name: string;
  placeholder: string;
  type?: string;
  required?: boolean;
  full?: boolean;
  inputMode?: "text" | "tel" | "email" | "numeric";
  error?: string;
  onChange?: () => void;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        inputMode={inputMode}
        onChange={onChange}
        aria-invalid={!!error}
        className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-brand-navy outline-none transition placeholder:text-muted-foreground focus:ring-4 ${
          error
            ? "border-red-400 focus:border-red-400 focus:ring-red-400/20"
            : "border-border focus:border-brand-orange focus:ring-brand-orange/20"
        }`}
      />
      {error && <p className="mt-1 text-[11px] text-red-300">{error}</p>}
    </div>
  );
}

export default function LeadForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | undefined>();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(undefined);
    setEmailError(undefined);

    const form = e.currentTarget;
    const fd = new FormData(form);

    if ((fd.get("botcheck") as string)?.length) return;

    const email = String(fd.get("email") || "").trim();
    if (isFreeEmail(email)) {
      setEmailError("Use um e-mail corporativo. E-mails pessoais não são aceitos.");
      return;
    }

    fd.set("access_key", WEB3FORMS_ACCESS_KEY);
    fd.set("subject", "Nova solicitação — Selectus RH");
    fd.set("from_name", "Site Selectus RH");

    setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        window.location.href = "/obrigado";
        return;
      } else {
        setStatus("error");
        setSubmitError(data?.message || "Não foi possível enviar. Tente novamente.");
      }
    } catch {
      setStatus("error");
      setSubmitError("Falha de conexão. Tente novamente em instantes.");
    }
  };

  return (
    <form
      id="form"
      onSubmit={onSubmit}
      className="rounded-2xl border border-white/10 bg-brand-navy p-6 shadow-[0_20px_60px_-30px_oklch(0.19_0.11_270/0.45)] md:p-8"
    >
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
        RESPOSTA EM 48H
      </div>
      <h2 className="font-display text-2xl font-bold tracking-tight text-white md:text-[26px]">
        Fale com um consultor
      </h2>
      <p className="mt-2 text-sm text-white/70">
        Preencha o formulário e receba uma proposta personalizada.
      </p>

      {status === "success" ? (
        <div className="mt-6 rounded-xl border border-white/10 bg-white/10 p-6 text-center">
          <Check className="mx-auto mb-2 h-8 w-8 text-brand-orange" />
          <p className="font-display font-semibold text-white">Recebido</p>
          <p className="mt-1 text-sm text-white/70">
            Um consultor entrará em contato em instantes.
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
          <input
            type="checkbox"
            name="botcheck"
            tabIndex={-1}
            autoComplete="off"
            style={{ display: "none" }}
          />
          <Field name="name" placeholder="Nome completo" required />
          <Field
            name="email"
            placeholder="E-mail corporativo"
            type="email"
            inputMode="email"
            required
            error={emailError}
            onChange={() => emailError && setEmailError(undefined)}
          />
          <Field name="cargo" placeholder="Cargo" required />
          <Field name="whatsapp" placeholder="WhatsApp" inputMode="tel" required />
          <Field name="empresa" placeholder="Nome da empresa" required />
          <Field name="ramo" placeholder="Ramo de atuação" required />
          <textarea
            name="mensagem"
            required
            rows={3}
            placeholder="Descreva sua demanda: nº de profissionais, funções, prazo…"
            className="w-full resize-none rounded-lg border border-border bg-white px-4 py-3 text-sm text-brand-navy outline-none transition placeholder:text-muted-foreground focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/20 md:col-span-2"
          />
          {submitError && (
            <p className="text-[12px] text-red-300 md:col-span-2">{submitError}</p>
          )}
          <div className="md:col-span-2">
            <OrangeCTA as="button" type="submit" full>
              {status === "sending" ? "Enviando…" : "Enviar solicitação"}
            </OrangeCTA>
          </div>
          <p className="text-[11px] leading-relaxed text-white/60 md:col-span-2">
            Ao enviar, você concorda em receber contato comercial da Selectus RH.
          </p>
        </div>
      )}
    </form>
  );
}